import * as XLSX from 'xlsx';

/**
 * MultiQuestionHelper
 *
 * Batch ("ask many at once") question mode for the chatbotexpand component:
 * toggling the multi-question panel, adding/removing rows, the Excel template
 * prompt + download, parsing an uploaded .xlsx/.csv into questions, and the
 * sequential queue that feeds each question into onSearchSubmit().
 *
 * Component state used: isMultiQuestionMode, multiQuestionList, showTemplatePrompt,
 * pendingFileInput, queuedQuestions, isProcessingQueue, searchForm.
 *
 * Extracted verbatim from chatbotexpand.component.ts (`this.` -> `comp.`);
 * behavior is unchanged.
 */
export class MultiQuestionHelper {

    static toggleMultiQuestionMode(comp: any): void {
        comp.isMultiQuestionMode = !comp.isMultiQuestionMode;
        if (!comp.isMultiQuestionMode) {
            comp.showTemplatePrompt = false;
            comp.pendingFileInput = null;
        }
        if (comp.isMultiQuestionMode && comp.multiQuestionList.length === 0) {
            comp.multiQuestionList = [''];
        }
    }

    static addMultiQuestion(comp: any): void {
        if (comp.multiQuestionList.length >= 10) {
            comp.showToast('You can only add up to 10 questions at a time.', 'info');
            return;
        }
        comp.multiQuestionList.push('');
        setTimeout(() => {
            const mqList = document.querySelector('.mq-list');
            if (mqList) {
                mqList.scrollTop = mqList.scrollHeight;
                const inputs = mqList.querySelectorAll('.mq-input');
                if (inputs.length > 0) {
                    (inputs[inputs.length - 1] as HTMLElement).focus();
                }
            }
        }, 0);
    }

    static promptForTemplate(comp: any, fileInput: HTMLInputElement): void {
        comp.pendingFileInput = fileInput;
        comp.showTemplatePrompt = true;
    }

    static handleTemplateSelection(comp: any, download: boolean): void {
        comp.showTemplatePrompt = false;
        if (download) {
            MultiQuestionHelper.downloadBatchTemplate(comp);
        } else if (comp.pendingFileInput) {
            comp.pendingFileInput.click();
        }
        comp.pendingFileInput = null;
    }

    private static downloadBatchTemplate(comp: any): void {
        const templateData = [
            ['Questions'],
            ['Aided brand awareness of bluehold'],
            ['familiarity of whitmore.']
        ];
        const worksheet = XLSX.utils.aoa_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
        XLSX.writeFile(workbook, 'Batch_Questions_Template.xlsx');
    }

    static onBatchExcelUpload(comp: any, event: Event): void {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e: any) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });
                const extractedQuestions: string[] = [];

                for (let i = 0; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (row && row.length > 0 && row[0]) {
                        const question = String(row[0]).trim();
                        // Skip the header row if it exists
                        if (question && question.toLowerCase() !== 'questions') {
                            extractedQuestions.push(question);
                        }
                    }
                }

                if (extractedQuestions.length > 0) {
                    if (comp.multiQuestionList.length === 1 && !comp.multiQuestionList[0].trim()) {
                        comp.multiQuestionList = extractedQuestions;
                    } else {
                        comp.multiQuestionList = [...comp.multiQuestionList, ...extractedQuestions];
                    }

                    if (comp.multiQuestionList.length > 10) {
                        comp.multiQuestionList = comp.multiQuestionList.slice(0, 10);
                        comp.showToast('You can only upload up to 10 questions at a time. Extra questions were removed.', 'info');
                    } else {
                        comp.showToast('Questions loaded from Excel successfully.', 'success');
                    }
                } else {
                    comp.showToast('No valid questions found in the Excel file.', 'info');
                }
            } catch (err) {
                console.error('Error parsing Excel file:', err);
                comp.showToast('Failed to parse the Excel file.', 'error');
            }
            target.value = '';
        };
        reader.readAsArrayBuffer(file);
    }

    static removeMultiQuestion(comp: any, index: number): void {
        comp.multiQuestionList.splice(index, 1);
        if (comp.multiQuestionList.length === 0) {
            comp.multiQuestionList = [''];
        }
    }

    static hasValidMultiQuestions(comp: any): boolean {
        return comp.multiQuestionList.some((q: string) => q && q.trim().length > 0);
    }

    static sendMultiQuestions(comp: any): void {
        const validQuestions = comp.multiQuestionList.map((q: string) => q.trim()).filter((q: string) => q.length > 0);

        if (validQuestions.length === 0) {
            comp.isMultiQuestionMode = false;
            return;
        }

        comp.queuedQuestions = validQuestions;
        comp.isProcessingQueue = true;
        comp.isMultiQuestionMode = false;
        comp.multiQuestionList = [''];

        comp.processNextQueuedQuestion();
    }

    static processNextQueuedQuestion(comp: any): void {
        if (comp.queuedQuestions.length > 0) {
            const nextQ = comp.queuedQuestions.shift();
            if (nextQ) {
                comp.searchForm.patchValue({ query: nextQ });
                comp.onSearchSubmit();
            }
        } else {
            comp.isProcessingQueue = false;
        }
    }
}
